/**
 * FilterSidebar — People page filter panel ("compact grid" design).
 *
 * Layout intent:
 *   ┌───────────────────────────────────────────┐
 *   │  Filters   3 active           Clear  ✕   │  ← header
 *   ├───────────────────────────────────────────┤
 *   │  ACTIVE FILTERS                  3 / 7   │  ← chip bar (only when ≥1)
 *   │  [Status: Current Staff ×] [Dept × …]    │
 *   ├───────────────────────────────────────────┤
 *   │  STATUS                       1 picked   │
 *   │  [ All ][Current][Active][Prob.][Term.]  │  ← segmented control
 *   │                                           │
 *   │  DEPARTMENT (full width)                  │
 *   │  [▾  All departments                   ]  │
 *   │                                           │
 *   │  POSITION              MANAGER            │
 *   │  [▾  All positions]    [▾  All managers]  │  ← 2-col compact selects
 *   │                                           │
 *   │  EMPLOYMENT            SENIORITY          │
 *   │  [▾  All types]        [▾  Senior]        │
 *   │                                           │
 *   │  GOOGLE ACCOUNT (full width)              │
 *   │  [  All  ][  Has  ][  No  ]               │  ← 3-segment
 *   └───────────────────────────────────────────┘
 *
 * Wiring:
 *   - Keeps the existing public API (`open`/`onOpenChange`/`filters`/`onFiltersChange`)
 *     so callers (`people.tsx`) don't need to change.
 *   - State management still goes through the universal `FilterProvider` — we
 *     just render a bespoke layout on top instead of using `FilterSheet`.
 *   - Option lists come from the same `useFilterOptions(...)` hook the universal
 *     system uses, so departments/positions/managers/etc stay in sync with the
 *     rest of the app.
 */

import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DEFAULT_EMPLOYEE_FILTERS,
  FilterProvider,
  employeeFilterConfig,
  useFilterContext,
  useFilterOptions,
  type EmployeeFilters,
} from '@/components/filters';
import { useCanViewCompensations } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { Trash2, X } from 'lucide-react';

// Re-export types for backward compatibility with existing callers.
export type { EmployeeFilters };
export const DEFAULT_FILTERS = DEFAULT_EMPLOYEE_FILTERS;

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface FilterSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: EmployeeFilters;
  onFiltersChange: (filters: EmployeeFilters) => void;
}

export function FilterSidebar({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
}: FilterSidebarProps) {
  return (
    <FilterProvider
      config={employeeFilterConfig}
      value={filters}
      onChange={onFiltersChange}
    >
      <CompactGridSheet open={open} onOpenChange={onOpenChange} />
    </FilterProvider>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const STATUS_SEGMENTS: { value: EmployeeFilters['status']; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'current_staff', label: 'Current' },
  { value: 'active', label: 'Active' },
  { value: 'probation', label: 'Probation' },
  { value: 'terminated', label: 'Terminated' },
];

const GOOGLE_SEGMENTS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Has account' },
  { value: 'false', label: 'No account' },
];

const COMPENSATION_SEGMENTS: { value: EmployeeFilters['compensationReview']; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'date_set', label: 'Date set' },
  { value: 'not_scheduled', label: 'Not scheduled' },
];

function CompactGridSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    filters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
  } = useFilterContext<EmployeeFilters>();

  const canViewCompensations = useCanViewCompensations();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:max-w-[420px] flex flex-col gap-0 p-0">
        {/* ── Header ──────────────────────────────────────────────────────
            Right padding (`pr-16`) reserves space for shadcn's built-in
            ✕ close button at `absolute top-4 right-4` PLUS our own trash
            icon mounted next to it (see below). Without the gutter the
            title row would slide under both glyphs. */}
        <SheetHeader className="border-b border-border/60 px-5 py-4 pr-16">
          <div className="flex items-center gap-2">
            <SheetTitle className="text-base font-semibold">Filters</SheetTitle>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
                {activeFilterCount} active
              </Badge>
            )}
          </div>
        </SheetHeader>

        {/* "Clear all" action — rendered as a trash icon button so it sits
            next to the Sheet's built-in ✕ glyph (which is `absolute top-4
            right-4`). Same visual rhythm as the close button, just one slot
            to the left at `right-12`. Hidden when nothing is active so the
            top-right corner stays uncluttered. */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Clear all filters"
            title="Clear all filters"
            className="absolute right-12 top-4 grid h-4 w-4 place-items-center rounded-sm text-muted-foreground opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Clear all filters</span>
          </button>
        )}

        {/* ── Active filter chips (only when ≥1 active) ────────────────── */}
        {hasActiveFilters && <ActiveFilterChips />}

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Status — segmented control */}
          <Section label="Status" hint={statusHint(filters.status)}>
            <SegmentedControl
              segments={STATUS_SEGMENTS}
              value={filters.status}
              onChange={(v) => setFilter('status', v as EmployeeFilters['status'])}
            />
          </Section>

          {/* 2-column grid for the dropdown filters */}
          <section className="grid grid-cols-2 gap-x-3 gap-y-4">
            <CompactSelect
              label="Department"
              source="departments"
              value={filters.departmentId}
              onChange={(v) => setFilter('departmentId', v)}
              placeholder="All departments"
              colSpan={2}
            />
            <CompactSelect
              label="Position"
              source="positions"
              value={filters.positionId}
              onChange={(v) => setFilter('positionId', v)}
              placeholder="All positions"
            />
            <CompactSelect
              label="Manager"
              source="managers"
              value={filters.managerId}
              onChange={(v) => setFilter('managerId', v)}
              placeholder="All managers"
            />
            <CompactSelect
              label="Employment"
              source="employmentTypes"
              value={filters.employmentTypeId}
              onChange={(v) => setFilter('employmentTypeId', v)}
              placeholder="All types"
            />
            <CompactSelect
              label="Seniority"
              source="seniorityLevels"
              value={filters.seniorityLevelId}
              onChange={(v) => setFilter('seniorityLevelId', v)}
              placeholder="All levels"
            />

            {/* Google account — segmented (3 segments) full width */}
            <div className="col-span-2">
              <Section label="Google account">
                <SegmentedControl
                  segments={GOOGLE_SEGMENTS}
                  value={filters.googleLinked}
                  onChange={(v) => setFilter('googleLinked', v)}
                />
              </Section>
            </div>
          </section>

          {canViewCompensations && (
            <Section label="Compensations">
              <SegmentedControl
                segments={COMPENSATION_SEGMENTS}
                value={filters.compensationReview}
                onChange={(v) =>
                  setFilter('compensationReview', v as EmployeeFilters['compensationReview'])
                }
              />
            </Section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Active filter chips
// ---------------------------------------------------------------------------

function ActiveFilterChips() {
  const { filters, setFilter } = useFilterContext<EmployeeFilters>();
  const canViewCompensations = useCanViewCompensations();
  const departments = useFilterOptions('departments');
  const positions = useFilterOptions('positions');
  const managers = useFilterOptions('managers');
  const employmentTypes = useFilterOptions('employmentTypes');
  const seniorityLevels = useFilterOptions('seniorityLevels');

  const findLabel = (
    options: { value: string; label: string }[],
    value: string,
    fallback: string
  ) => options.find((o) => o.value === value)?.label ?? fallback;

  type Chip = { key: keyof EmployeeFilters; label: string; reset: () => void };
  const chips: Chip[] = [];

  if (filters.status !== DEFAULT_EMPLOYEE_FILTERS.status) {
    const seg = STATUS_SEGMENTS.find((s) => s.value === filters.status);
    chips.push({
      key: 'status',
      label: `Status: ${seg?.label ?? filters.status}`,
      reset: () => setFilter('status', DEFAULT_EMPLOYEE_FILTERS.status),
    });
  }
  if (filters.departmentId !== 'all') {
    chips.push({
      key: 'departmentId',
      label: `Department: ${findLabel(departments.options, filters.departmentId, filters.departmentId)}`,
      reset: () => setFilter('departmentId', 'all'),
    });
  }
  if (filters.positionId !== 'all') {
    chips.push({
      key: 'positionId',
      label: `Position: ${findLabel(positions.options, filters.positionId, filters.positionId)}`,
      reset: () => setFilter('positionId', 'all'),
    });
  }
  if (filters.managerId !== 'all') {
    chips.push({
      key: 'managerId',
      label: `Manager: ${findLabel(managers.options, filters.managerId, filters.managerId)}`,
      reset: () => setFilter('managerId', 'all'),
    });
  }
  if (filters.employmentTypeId !== 'all') {
    chips.push({
      key: 'employmentTypeId',
      label: `Employment: ${findLabel(employmentTypes.options, filters.employmentTypeId, filters.employmentTypeId)}`,
      reset: () => setFilter('employmentTypeId', 'all'),
    });
  }
  if (filters.seniorityLevelId !== 'all') {
    chips.push({
      key: 'seniorityLevelId',
      label: `Seniority: ${findLabel(seniorityLevels.options, filters.seniorityLevelId, filters.seniorityLevelId)}`,
      reset: () => setFilter('seniorityLevelId', 'all'),
    });
  }
  if (filters.googleLinked !== 'all') {
    const seg = GOOGLE_SEGMENTS.find((g) => g.value === filters.googleLinked);
    chips.push({
      key: 'googleLinked',
      label: `Google: ${seg?.label ?? filters.googleLinked}`,
      reset: () => setFilter('googleLinked', 'all'),
    });
  }
  if (canViewCompensations && filters.compensationReview !== DEFAULT_EMPLOYEE_FILTERS.compensationReview) {
    const seg = COMPENSATION_SEGMENTS.find((s) => s.value === filters.compensationReview);
    chips.push({
      key: 'compensationReview',
      label: `Compensations: ${seg?.label ?? filters.compensationReview}`,
      reset: () => setFilter('compensationReview', DEFAULT_EMPLOYEE_FILTERS.compensationReview),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="border-b border-border/60 bg-muted/30 px-5 py-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          Active filters
        </span>
        <span className="font-mono text-[11px] text-muted-foreground">{chips.length} / {canViewCompensations ? 8 : 7}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <span
            key={chip.key}
            className="inline-flex h-6 items-center gap-1.5 rounded-md border border-hygge/20 bg-hygge/10 pl-2 pr-1 text-[12px] font-medium text-hygge"
          >
            <span className="max-w-[180px] truncate">{chip.label}</span>
            <button
              type="button"
              onClick={chip.reset}
              aria-label={`Remove ${chip.label}`}
              className="grid h-4 w-4 place-items-center rounded-sm transition-colors hover:bg-hygge/15"
            >
              <X className="h-2.5 w-2.5" strokeWidth={2.5} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Section({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {label}
        </label>
        {hint && <span className="font-mono text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

interface SegmentedControlProps<T extends string> {
  segments: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}

function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
}: SegmentedControlProps<T>) {
  return (
    <div
      className="grid gap-0.5 rounded-md border border-border/60 bg-muted/40 p-0.5"
      style={{ gridTemplateColumns: `repeat(${segments.length}, minmax(0, 1fr))` }}
    >
      {segments.map((seg) => {
        const active = seg.value === value;
        return (
          <button
            key={seg.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(seg.value)}
            className={cn(
              'h-7 rounded-[5px] text-[11.5px] font-medium transition-colors cursor-pointer',
              active
                ? 'bg-hygge text-background shadow-[0_1px_0_rgba(0,0,0,0.05)]'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}

interface CompactSelectProps {
  label: string;
  source: 'departments' | 'positions' | 'managers' | 'employmentTypes' | 'seniorityLevels';
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  colSpan?: 1 | 2;
}

function CompactSelect({
  label,
  source,
  value,
  onChange,
  placeholder,
  colSpan = 1,
}: CompactSelectProps) {
  const { options, isLoading } = useFilterOptions(source);
  const active = value !== 'all';

  return (
    <div className={cn(colSpan === 2 && 'col-span-2')}>
      <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </label>
      {isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger
            className={cn(
              'h-8 text-[13px] transition-colors',
              active
                ? 'border-hygge/45 bg-hygge/[0.06] font-medium text-hygge ring-1 ring-inset ring-hygge/20'
                : 'text-muted-foreground'
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{placeholder}</SelectItem>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
                {opt.count !== undefined && (
                  <span className="ml-1.5 text-muted-foreground">({opt.count})</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusHint(status: EmployeeFilters['status']): string {
  return status === DEFAULT_EMPLOYEE_FILTERS.status ? 'default' : '1 picked';
}
